import { useEffect, useState } from "react";
import api, { apiErr, fmtMoney, fmtDate } from "@/lib/api";
import { PageHeader, Loader } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ChevronRight, ChevronDown, CheckCircle2, Circle, Link2 } from "lucide-react";
import { toast } from "sonner";

export default function Lettrage() {
  const [type, setType] = useState("clients");
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState({});

  const load = () => { setData(null); api.get("/accounting/lettrage", { params: { type } }).then((r) => setData(r.data)); };
  useEffect(() => { load(); }, [type]);

  const toggle = async (doc_id, payment_id) => {
    try { await api.post("/accounting/lettrage/toggle", { type, doc_id, payment_id: payment_id || null }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const autoLettrage = async () => {
    try { const { data: res } = await api.post(`/accounting/lettrage/auto?type=${type}`); toast.success(res.message); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      <PageHeader title="Lettrage clients / fournisseurs" subtitle="Rapprochement facture ↔ paiements — pointage manuel des acomptes">
        <div className="flex gap-1">
          <Button size="sm" variant={type === "clients" ? "default" : "outline"} onClick={() => setType("clients")} data-testid="lettrage-clients" className={type === "clients" ? "bg-brand-600 text-white" : ""}>Clients</Button>
          <Button size="sm" variant={type === "fournisseurs" ? "default" : "outline"} onClick={() => setType("fournisseurs")} data-testid="lettrage-fournisseurs" className={type === "fournisseurs" ? "bg-brand-600 text-white" : ""}>Fournisseurs</Button>
          <Button size="sm" onClick={autoLettrage} data-testid="lettrage-auto" className="bg-amber-500 hover:bg-amber-600 text-white ml-2"><Link2 className="h-3 w-3 mr-1" />Lettrage auto</Button>
        </div>
      </PageHeader>

      {!data ? <Loader /> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <div className="rounded-xl border bg-white p-4"><p className="text-[11px] uppercase font-mono text-slate-400">Total facturé</p><p className="font-mono font-bold text-lg mt-1">{fmtMoney(data.total)}</p></div>
            <div className="rounded-xl border bg-white p-4"><p className="text-[11px] uppercase font-mono text-slate-400">Lettré (payé)</p><p className="font-mono font-bold text-lg mt-1 text-emerald-700">{fmtMoney(data.lettre)}</p></div>
            <div className="rounded-xl border bg-white p-4"><p className="text-[11px] uppercase font-mono text-slate-400">Restant dû</p><p className="font-mono font-bold text-lg mt-1 text-amber-700">{fmtMoney(data.restant)}</p></div>
            <div className="rounded-xl border bg-white p-4"><p className="text-[11px] uppercase font-mono text-slate-400">Lettrés / Non lettrés</p><p className="font-mono font-bold text-lg mt-1">{data.nb_lettres} / {data.nb_non_lettres}</p></div>
          </div>

          <div className="rounded-xl border bg-white overflow-hidden"><div className="overflow-x-auto scrollbar-thin">
            <Table>
              <TableHeader><TableRow className="bg-slate-50"><TableHead></TableHead><TableHead>N°</TableHead><TableHead>Tiers</TableHead><TableHead>Date</TableHead><TableHead className="text-right">Montant</TableHead><TableHead className="text-right">Payé</TableHead><TableHead className="text-right">Solde</TableHead><TableHead>Lettrage</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {data.rows.map((r) => {
                  const isOpen = expanded[r.doc_id];
                  return (
                    <>
                      <TableRow key={r.doc_id} data-testid={`lettrage-row-${r.numero}`} className="hover:bg-slate-50">
                        <TableCell>{r.payments.length > 0 && <button onClick={() => setExpanded((s) => ({ ...s, [r.doc_id]: !s[r.doc_id] }))} data-testid={`lettrage-expand-${r.numero}`}>{isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</button>}</TableCell>
                        <TableCell className="font-mono text-xs text-slate-500">{r.numero}</TableCell>
                        <TableCell className="font-semibold">{r.tiers}</TableCell>
                        <TableCell className="font-mono text-xs">{fmtDate(r.date)}</TableCell>
                        <TableCell className="text-right font-mono text-xs">{fmtMoney(r.montant)}</TableCell>
                        <TableCell className="text-right font-mono text-xs text-emerald-600">{fmtMoney(r.paye)}</TableCell>
                        <TableCell className="text-right font-mono text-xs font-bold">{fmtMoney(r.solde)}</TableCell>
                        <TableCell><StatusBadge value={r.statut} /></TableCell>
                        <TableCell><Button size="sm" variant={r.lettrage_manuel ? "default" : "outline"} onClick={() => toggle(r.doc_id, null)} data-testid={`lettrage-manual-${r.numero}`} className={r.lettrage_manuel ? "bg-brand-600 text-white h-7 text-xs" : "h-7 text-xs"}><Link2 className="h-3 w-3 mr-1" />{r.lettrage_manuel ? "Lettré" : "Lettrer"}</Button></TableCell>
                      </TableRow>
                      {isOpen && r.payments.map((p) => (
                        <TableRow key={p.id} className="bg-slate-50/60" data-testid={`lettrage-payment-${p.id}`}>
                          <TableCell></TableCell>
                          <TableCell colSpan={3} className="text-xs text-slate-500 pl-6">Paiement {p.mode ? `· ${p.mode}` : ""} — {fmtDate(p.date)}</TableCell>
                          <TableCell></TableCell>
                          <TableCell className="text-right font-mono text-xs text-emerald-600">{fmtMoney(p.montant)}</TableCell>
                          <TableCell></TableCell>
                          <TableCell colSpan={2}>
                            <button onClick={() => toggle(r.doc_id, p.id)} data-testid={`lettrage-payment-toggle-${p.id}`} className="flex items-center gap-1.5 text-xs">
                              {p.lettre ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <Circle className="h-4 w-4 text-slate-300" />}
                              <span className={p.lettre ? "text-emerald-600" : "text-slate-400"}>{p.lettre ? "Pointé" : "À pointer"}</span>
                            </button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </>
                  );
                })}
                {data.rows.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-slate-400 py-6">Aucune pièce.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div></div>
        </>
      )}
    </div>
  );
}
